import React, {createContext, PropsWithChildren, useCallback, useContext, useEffect, useRef, useState} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Base64 from 'base64-js';
import {CreativeType, ForecastingStatus, POIType} from '../data/__generated__/globalTypes';
import { useQuery } from '@apollo/client';
import { PollForecastingQuery } from '../data/__generated__/PollForecastingQuery';
import { POLL_FORECASTING_QUERY } from '../data/ForecastingResults';
import { days, hours } from './forecasting-panel/forecasting-filters/TimeOfWeek';
import { ForecastingSSP } from './forecasting-panel/forecasting-filters/SSP';

export type VisualizationType =
    | 'numberOfScreens'
    | 'potentialSpend'
    | 'averageCpm'
    | 'numberOfImpressions'
    | 'numberOfAudienceImpressions';

export type PointOfInterest = {
    latitude: number,
    longitude: number,
    radiusInMeters?: number
}

export type ForecastingSettingsValue = {
    ssp: ForecastingSSP[];
    dateRange: [string | null, string | null];
    hourOfWeekFilter: boolean[][];
    publisherFilter: string[] | null;
    countryFilter: string[] | null;
    regionFilter: string[] | null;
    metroFilter: { label: string, value: string }[] | null;
    cityFilter: string[] | null;
    zipFilter: string[] | null;
    creativeTypes: CreativeType[] | null;
    venueTypeFilter: { label: string, value: string }[] | null;
    enableAudienceTargeting: boolean;
    audienceId: string;
    includeAudienceTier1: boolean;
    includeAudienceTier2: boolean;
    includeAudienceTier3: boolean;
    includeAudienceTier4: boolean;
    winRate: number;
    poiList: PointOfInterest[] | null;
    polygonList: PointOfInterest[][] | null;
    poiType: POIType | null;
};

type InitialValues = {
    forecastingSettingsValues : ForecastingSettingsValue;
    visualizationSettingsValue: VisualizationType;
    forecastingIdValue: string | null;
}

/**********************************************************************************************************************/
/************ Forecasting Settings: track user selected data filters and visualisation settings***** ******************/
/**********************************************************************************************************************/
const addDays = (numberOfDays: number) => {
    const today = new Date();
    today.setDate(today.getDate() + numberOfDays);
    return today;
};

const DefaultForecastingSettingsValue: ForecastingSettingsValue = {
    ssp: ['broadsign'],
    dateRange: [new Date().toISOString(), addDays(7).toISOString()],
    hourOfWeekFilter: days.map(() => hours.map(() => true)),
    publisherFilter: null,
    countryFilter: null,
    regionFilter: null,
    metroFilter: null,
    cityFilter: null,
    zipFilter: null,
    creativeTypes: null,
    venueTypeFilter: null,
    enableAudienceTargeting: false,
    audienceId: '',
    includeAudienceTier1: true,
    includeAudienceTier2: true,
    includeAudienceTier3: true,
    includeAudienceTier4: true,
    winRate: 0.8,
    poiList: null,
    polygonList: null,
    poiType: null,
};

const DefaultInitialValues: InitialValues = {
    forecastingSettingsValues: DefaultForecastingSettingsValue,
    visualizationSettingsValue: 'numberOfImpressions',
    forecastingIdValue: null,
}

function useInitialSettings(): InitialValues {
    const { settings } = useParams();
    if (settings != null) {
        try {
            let decoder = new TextDecoder();
            let ValuesFromParams = JSON.parse(decoder.decode(Base64.toByteArray(settings)));
            if (typeof ValuesFromParams === 'object') {
                const {forecastingId, visualizationType, ...rest} = ValuesFromParams;
                return {
                    forecastingSettingsValues: rest,
                    visualizationSettingsValue: visualizationType,
                    forecastingIdValue: forecastingId
                }
            }
        } catch (e) {
            console.log('Exception in decoding ValuesFromParams. Will use DefaultInitialValues');
        }
    }
    return DefaultInitialValues;
}

const ForecastingSettingsContext = createContext({
    forecastingSettings: DefaultForecastingSettingsValue,
    updateForecastingSetting: (updatedSettings: Partial<ForecastingSettingsValue>) => {}
});
export const useForecastingSettings = () => useContext(ForecastingSettingsContext);

const VisualizationSettingsContext = createContext({
    visualizationType: 'numberOfImpressions',
    updateVisualizationSetting: (updatedSettings: VisualizationType) => {}
})
export const useVisualizationSettings = () => useContext(VisualizationSettingsContext); 

/**********************************************************************************************************************/
/************ Forecasting State: represents results polling status of any given forecasting run ***********************/
/**********************************************************************************************************************/
// null means not relevant because there's no forecasting id. unknown means we will find out soon.
export type ForecastingState = null | 'unknown' | 'transient' | 'loaded' | 'failed';

const ForecastingStateContext = createContext<{
    forecastingState: ForecastingState | null,
    forecastingId: string | null,
    updateForecastingId: (updatedForecastingId: string | null) => void
}>({
    forecastingState: null,
    forecastingId: null,
    updateForecastingId: (updatedForecastingId: string | null) => {}
});
export const useForecastingState = () => useContext(ForecastingStateContext);
/**********************************************************************************************************************/

export default function ForecastingContextProvider({ children }: PropsWithChildren<{}>) {
    const {forecastingSettingsValues, forecastingIdValue, visualizationSettingsValue} = useInitialSettings();
    const [forecastingId, setForecastingId] = useState(forecastingIdValue);
    const [forecastingSettings, setForecastingSettings] = useState(forecastingSettingsValues);
    const [visualizationSettings, setVisualizationSettings] = useState(visualizationSettingsValue);
    const navigate = useNavigate();
    const encoder = new TextEncoder();

    const updateEncodedSettings = (updatedSettings: InitialValues) => {
        const {forecastingSettingsValues, forecastingIdValue, visualizationSettingsValue} = updatedSettings;
        let encodedSettings = Base64.fromByteArray(encoder.encode(JSON.stringify({...forecastingSettingsValues, forecastingId: forecastingIdValue, visualizationType: visualizationSettingsValue})));
        navigate('/' + encodedSettings);
    }

    const updateAndCacheForecastingSettings = (updatedForecastingSettings: Partial<ForecastingSettingsValue>) => {
        const nextForecastingSettings = {...forecastingSettings, ...updatedForecastingSettings};

        updateEncodedSettings({forecastingSettingsValues: nextForecastingSettings, forecastingIdValue: forecastingId, visualizationSettingsValue: visualizationSettings})

        setForecastingSettings(nextForecastingSettings);
    };

    const updateAndCacheForecastingId = (updatedForecastingId: string | null) => {
        updateEncodedSettings({
            forecastingSettingsValues: forecastingSettings,
            visualizationSettingsValue: visualizationSettings,
            forecastingIdValue: updatedForecastingId
        })

        setForecastingId(updatedForecastingId);
    };

    const updateAndCacheVisualizationSettings = (updatedVisualizationSettings: VisualizationType) => {
        updateEncodedSettings({
            forecastingSettingsValues: forecastingSettings,
            visualizationSettingsValue: updatedVisualizationSettings,
            forecastingIdValue: forecastingId
        })

        setVisualizationSettings(updatedVisualizationSettings)
    };

    const parseForecastingState = (forecastingStatus: ForecastingStatus | null | undefined): ForecastingState => {
        const forecastingStatusToState: Readonly<Record<ForecastingStatus, ForecastingState>> = {
            LOADED: 'loaded',
            FAILED: 'failed',
            NOT_FOUND: 'failed',
            LOADING: 'transient',
            NOT_LOADED: 'transient'
        };

        return (forecastingStatus === undefined || forecastingStatus === null)
            ? null
            : forecastingStatusToState[forecastingStatus];
    };

    const previousForecastingState = useRef<ForecastingState | null>(null);
    const {
        loading: initialPollLoading,
        data: initialPollData
    } = useQuery<PollForecastingQuery>(POLL_FORECASTING_QUERY, {
        variables: { forecastingId: forecastingId, longPoll: false },
        skip: forecastingId === null
    });
    const { data: pollData, stopPolling } = useQuery<PollForecastingQuery>(POLL_FORECASTING_QUERY, {
        variables: { forecastingId: forecastingId, longPoll: true },
        skip: forecastingId === null || !initialPollData,
        pollInterval: previousForecastingState.current === 'transient' ? 1_000 : 3_600_000,
        onCompleted: data => {
            previousForecastingState.current = parseForecastingState(data?.forecastingResults.data?.status);
        }
    });

    useEffect(() => {
        if (forecastingId == null) {
            stopPolling();
        }
    }, [forecastingId]);
    
    const state = parseForecastingState((pollData ?? initialPollData)?.forecastingResults.data?.status) ?? (initialPollLoading ? 'unknown' : null);

    return (
            <VisualizationSettingsContext.Provider value={{
                visualizationType: visualizationSettings,
                updateVisualizationSetting: updateAndCacheVisualizationSettings
            }}>
                <ForecastingSettingsContext.Provider value={{
                    forecastingSettings,
                    updateForecastingSetting: updateAndCacheForecastingSettings
                }}>
                    <ForecastingStateContext.Provider value={{
                        forecastingState: state,
                        forecastingId: forecastingId,
                        updateForecastingId: updateAndCacheForecastingId
                    }}>
                        {children}
                    </ForecastingStateContext.Provider>
                </ForecastingSettingsContext.Provider>
            </VisualizationSettingsContext.Provider>
    );
}
